import {Alert, Box, Button, CircularProgress, Modal, Typography} from "@mui/material";
import {Warning} from "@mui/icons-material";
import {useState} from "react";
import useToken from "../../useToken";
import {TimeManager} from "../../timemanager";
import CourseImport from "./CourseImport";
import {OverlayScrollbarsComponent} from "overlayscrollbars-react";

export default function CanvasImport(props) {
    const {
        open,
        onClose,
        mobile,
        refreshTasks,
    } = props;

    const [token] = useToken();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [importResults, setImportResults] = useState(null);

    const beginImport = () => {
        setLoading(true);

        TimeManager.import.canvas(token)
            .then((results) => results.map((course) => {
                course.assignments.forEach((assignment) => {
                    assignment.name = `${course.code} | ${assignment.name}`;
                    assignment.start = assignment.created;
                    assignment.end = assignment.due;
                    assignment.included = false;

                    let end = assignment.end ? new Date(assignment.end) : null;
                    let start = assignment.start ? new Date(assignment.start) : null;

                    if (start && end) {
                        start.setTime(Math.max(end.getTime() - 1000*60*60*24*7, start.getTime()));
                    }
                    else if (end) {
                        start = new Date(end.getTime() - 1000*60*60*24*7);
                    }
                    else if (start) {
                        end = new Date(start.getTime() + 1000*60*60*24*7);
                    }

                    assignment.start = start;
                    assignment.end = end;
                });

                return course;
            }))
            .then(setImportResults)
            .catch((err) => setAlert(err.message))
            .finally(() => setLoading(false));
    };

    const onChange = (courseIndex) => {
        return (assignmentIndex, assignment) => {
            setImportResults((results) => {
                const newResults = [...results];
                newResults[courseIndex].assignments[assignmentIndex] = assignment;
                return results;
            });
        };
    };

    const createTasks = () => {
        setLoading(true);

        const assignments = [];

        importResults.forEach((course) => {
            course.assignments.forEach((assignment) => {
                if (assignment.included) {
                    assignments.push({
                        name: assignment.name,
                        start: assignment.start,
                        end: assignment.end,
                        link: `CANVAS:${course.id}:${assignment.id}`,
                        description: `URL: ${assignment.url}\nPoints: ${assignment.points}`,
                    });
                }
            });
        });

        TimeManager.time.task.batch(assignments, token)
            .then(() => onClose?.())
            .then(refreshTasks)
            .finally(() => setLoading(false));
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
            >
                <div>
                    <OverlayScrollbarsComponent
                        options={{
                            scrollbars: {
                                theme: 'os-theme-light'
                            }
                        }}
                        style={{
                            padding: '4rem',
                            position: 'absolute',
                            top: mobile ? '0' : '50%',
                            left: mobile ? '0' : '50%',
                            transform: !mobile && 'translate(-50%, -50%)',
                            width: mobile ? '100vw' : 'calc(100vw - 100px)',
                            height: mobile ? '100vh' : 'calc(100vh - 100px)',
                            bgcolor: 'background.paper',
                            boxShadow: '24rem',
                            borderRadius: '5px',
                            overflowY: 'hidden',
                            background: 'rgb(18, 18, 18)',
                        }}
                    >
                        <Typography
                            variant="h1"
                            component="h1"
                            mb="30px"
                            fontSize="18pt"
                            fontWeight="400"
                        >
                            Import Canvas assignments
                        </Typography>
                        {
                            loading ?
                                <>
                                    <CircularProgress
                                        sx={{
                                            display: 'block',
                                            m: '0 auto'
                                        }}
                                    />
                                    <Typography
                                        m="15px 0"
                                        fontSize="12pt"
                                    >
                                        Import will likely take several seconds to complete.
                                    </Typography>
                                </>
                                :
                                importResults ?
                                    <>
                                        <Box
                                            sx={{
                                                height: '600px',
                                                maxHeight: 'calc(100vh - 170px)',
                                                overflowY: 'scroll',
                                                mb: '30px',
                                            }}
                                        >
                                            {
                                                importResults.map((course, index) => (
                                                    course.assignments.length > 0 ?
                                                        <CourseImport
                                                            key={course.name}
                                                            course={course}
                                                            onChange={onChange(index)}
                                                        /> :
                                                        <div key={course.name}></div>
                                                ))
                                            }
                                        </Box>

                                        <Button
                                            variant="contained"
                                            onClick={createTasks}
                                            sx={{
                                                mr: '10px',
                                            }}
                                        >
                                            Create tasks
                                        </Button>

                                        <Button
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                    :
                                    <>
                                        {alert &&
                                            <Alert
                                                icon={<Warning/>}
                                                onClose={() => setAlert(null)}
                                                color="error"
                                                sx={{
                                                    mb: '20px',
                                                }}
                                            >
                                                {alert}
                                            </Alert>
                                        }

                                        <Button
                                            variant="contained"
                                            onClick={beginImport}
                                            sx={{
                                                mr: '10px',
                                            }}
                                        >
                                            Start import
                                        </Button>

                                        <Button
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                        }
                    </OverlayScrollbarsComponent>
                </div>
            </Modal>
        </>
    );
}